import { useCallback, useEffect, useRef, useState } from 'react';
import { api, isAbort } from '../../shell/bridge';
import { authApi, authErrorMessage, exportMeRequest, getAuthSession, getCoupleCode } from '../../lib/api';
import { WHATS_NEW, type WhatNewEntry } from '../../lib/whatsNew';
import { Switch } from '../../ui/Switch';
import { AgentSettings } from '../AI/AgentSettings';
import { SubTabs, SubPanel, type SubTabDef } from '../../components/SubTabs';
import { InboxAddress } from '../../components/InboxAddress';
import { InviteCard } from '../../components/InviteCard';
import { SharePearCard } from '../../components/SharePearCard';
import { Interests } from './Interests';
/* Card 9QADJv3CdiOh — the closed list of reply inks (every entry measured
   AA on the mustard partner bubble) and the key→hex resolver the preview
   and the Thread both use. */
import { REPLY_COLOURS, replyColourHex } from '../../../../shared/types';
import './settings.css';

type Toggles = Record<string, boolean>;

interface Holiday {
  key: string;
  label: string;
  enabled: boolean;
  leadDays: number;
  /** Custom occasions only: MM-DD, repeats every year. Null until set. */
  date?: string | null;
}

interface Profile {
  memberId: string;
  birthday: string | null;
  /** The chat name — what the partner sees (card yFLCjm7ONIEc). */
  displayName: string | null;
  /** The reply-text colour key picked in this row (card 9QADJv3CdiOh) —
   *  null until chosen. */
  replyColour?: string | null;
}

interface AccountName {
  name: string | null;
}

interface CoupleSettings {
  anniversary: string | null;
  holidays: Holiday[];
  inboxTodoMode: 'suggest' | 'auto';
  /** Task Assist (spec kZX3gsJkKaTH). Optional so an older payload still
   *  renders: absent reads as off, never as on. */
  taskAssist?: { enabled: boolean; offer: boolean };
}

interface Props {
  onClose: () => void;
  /**
   * Leave this couple. Returns null on success, or the sentence to show —
   * the server can refuse (card Vf23eSKKxDhX), and a refusal keeps the
   * person in the couple with the reason rather than half-leaving them.
   */
  onLeave: () => Promise<string | null>;
  /** End this device's session without touching the couple (email 2026-10-07:
   *  "there's no sign out only leave"). Leave stays the destructive one. */
  onSignOut?: () => void;
  /** Which pinned section is showing (card DI31pLXCFc2C). Held in App so a
   *  deep link (#settings-<tab>) and the More menu can select it, and so a
   *  visit back keeps where you were — same contract as Life / Us tabs. */
  tab: string;
  onTabChange: (key: string) => void;
}

const isDate = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v);

/** Custom occasions are `custom-1`..`custom-N` — the DO renumbers on save. */
const isCustomKey = (key: string) => /^custom(-\d+)?$/.test(key);
/** Mirrors MAX_CUSTOM_OCCASIONS in shared/types.ts — the DO rejects past it. */
const MAX_CUSTOM = 5;
/** The four common lead times as stepper anchors (card zkDivBtVIpAP). */
const PRESET_LEADS = [7, 14, 30, 60];
/** A date picker needs a year; 2000 is a leap year, so 29 Feb is pickable. */
const DATE_YEAR = '2000';
/** Build stamp injected by vite define — see Settings → Build. */
const BUILD_ID = typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : 'dev';

/** Card QR9phRSRQHNx — the build reference is year.month.build, and the
 *  Build card carries what's new in this one (JitsLab's About pattern).
 *
 *  Exactly ONE entry, always (founder 2026-10-17: "What's new listed twice").
 *  The old filter matched the rolling entry AND the frozen BUILD_ID entry and
 *  rendered both, and because rolling renders its heading as BUILD_ID the two
 *  headings were identical — one empty, one not. Rolling's items are folded
 *  in front of the frozen ones instead, so a release that has not yet frozen
 *  its list still shows, and an empty pair renders nothing at all. */
const WHATS_NEW_FOR_BUILD: WhatNewEntry[] = (() => {
  const rolling = WHATS_NEW.find(e => e.version === 'rolling');
  const frozen = WHATS_NEW.filter(e => e.version === BUILD_ID);
  const items = [...(rolling?.items ?? []), ...frozen.flatMap(e => e.items)];
  if (items.length === 0) return [];
  return [{
    version: BUILD_ID,
    date: frozen[0]?.date ?? rolling?.date ?? '',
    items,
  }];
})();

/** The server's honest reasons, in the app's voice (card 3UwJvUtiirGN).
 *  Anything unrecognised still says something true rather than "error". */
const RECAP_REASONS: Record<string, string> = {
  toggle_off: 'Turn the recap on first.',
  not_due: 'Next recap due — one a week.',
  not_paired: 'A recap needs you both — pair up first.',
  no_email: 'Add an email to your account to get the recap.',
  mail_off: 'Email is not configured in this environment.',
  mail_failed: 'Email Could Not Be Sent. Nothing Was Marked as Sent, so It Will Try Again.',
  nothing_to_report: 'A quiet week — nothing to recap yet.',
};
const recapReason = (reason: string): string => RECAP_REASONS[reason] ?? 'The recap could not be sent just now.';

/** Card si3nCYgCqe4g — the row says WHEN the next recap goes, not only that
 *  the toggle is on. `nextDueAt` comes from the server's own window clock, so
 *  the date here and the send that actually fires cannot drift apart. */
const recapWhen = (r: { due: boolean; nextDueAt: number }): string => {
  if (r.due || !r.nextDueAt) return 'Due now — it goes out next time you open Pear.';
  const day = new Date(r.nextDueAt).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  return `Next one on ${day}.`;
};

/**
 * Pinned sections (card DI31pLXCFc2C, Jonathan email 2026-10-12): Settings
 * was one long page; it is now tabbed submenus like JitsLab — one section on
 * screen at a time, ordered for comprehension: identity → relationship →
 * agent → billing → account. Same SubTabs row every other screen uses.
 */
const SETTINGS_TABS: SubTabDef[] = [
  { key: 'you', label: 'You' },
  { key: 'us', label: 'Us' },
  { key: 'concierge', label: 'Concierge' },
  { key: 'plan', label: 'Plan' },
  { key: 'about', label: 'About' },
];
/** Deep-link vocabulary: #settings-<key> opens Settings on that section. */
export const SETTINGS_TAB_KEYS = SETTINGS_TABS.map(t => t.key);
export const settingsTabFromHash = (hash: string): string | null => {
  const m = /^#settings-([a-z]+)$/.exec(hash);
  return m && SETTINGS_TAB_KEYS.includes(m[1]) ? m[1] : null;
};


/**
 * Settings screen (cards 0xhNPdS42FTh + yQtaPZmm4vzI, Jonathan 2026-10-02).
 *
 * Three groups, in the order Jonathan asked for them: the agent behaviour
 * control (moved here verbatim from the AI screen), the individual half (a
 * birthday is one person's), the joint half (anniversary + gift lead times
 * both of you share), and Leave last because it is the one that ends things.
 *
 * Individual and joint are separate endpoints because they are separate rows
 * in the DO: your birthday cannot be overwritten by your partner's PUT.
 */
interface ExtendedProps extends Props {
  /** Close settings and open the AI panel in place. */
  onOpenAI?: () => void;
  /** Close settings and open the full Help inventory (card FiLhF6LRwAAP). */
  onOpenHelp?: () => void;
  /** Close settings and open the on-demand tour (card yOszUUb0Nnc2). */
  onOpenTour?: () => void;
  /** The server-issued pair code, still unaccepted (Jonathan, pm admin
   *  2026-10-13: the invite has to be reachable from a screen people
   *  actually reopen, not only the moment it is minted). */
  pendingCode?: string | null;
  /** True while the couple is still one member — the invite's whole life. */
  awaitingPartner?: boolean;
  /** Mail wired on this server: false hides the emailed invite entirely. */
  mailEnabled?: boolean;
}

/* ── Notifications (card HWrVePJ5qW4d, follow-on slice 2) ──────────────
 * The preference surface the card asks for: one switch per trigger the
 * worker can fire. Preferences are per MEMBER (the same row drives every
 * device you own) and the defaults are the card's — question and partner
 * on, trial off.
 *
 * The switches alone would be a lie on a device that never subscribed, so
 * this section owns the subscription too: "Turn on" asks for permission,
 * subscribes with the worker's VAPID key and registers the device. The
 * prompt is user-initiated only (never on load), and a browser that cannot
 * do Web Push — insecure origin, no service worker, iOS Safari outside a
 * home-screen install — reads one plain sentence instead of controls that
 * would do nothing.
 *
 * What the payload will say is deliberately thin (see the worker): the
 * notification names an event, never your words, so the copy here promises
 * nothing about content. On iPhone the affordance is slice 3's install
 * steps: Web Push there exists only for a home-screen install, and Safari
 * never fires beforeinstallprompt, so instructions are the only honest
 * control (iosSafariNeedsInstall above decides when they show). */

type PushKind = 'question' | 'partner' | 'trial';
interface PushPrefs { question: boolean; partner: boolean; trial: boolean }

const PUSH_ROWS: { kind: PushKind; label: string; note: string }[] = [
  { kind: 'question', label: 'Daily question', note: 'When a new question lands for you both.' },
  { kind: 'partner', label: 'Partner activity', note: 'When your partner messages and you are not in the app.' },
  { kind: 'trial', label: 'Trial ending', note: 'A nudge before the free trial runs out. Off unless you want it.' },
];

/** base64url VAPID key → the Uint8Array PushManager.subscribe wants. */
function vapidKeyBytes(key: string): Uint8Array {
  const pad = '='.repeat((4 - (key.length % 4)) % 4);
  const b64 = (key + pad).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

/**
 * Does this iPhone/iPad need an install before notifications can exist?
 *
 * iOS Safari exposes Web Push ONLY to a web app added to the home screen
 * (16.4+), and it never fires `beforeinstallprompt` — there is no button the
 * browser will show for us, so the only honest affordance is the three-step
 * Share → Add to Home Screen instruction. An already-installed app reads the
 * normal switches, so both `navigator.standalone` and the standalone display
 * mode are checked (iPadOS reports a desktop UA, hence the mode checks too).
 */
function iosSafariNeedsInstall(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return false;
  if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return false;
  try {
    if (window.matchMedia('(display-mode: standalone)').matches) return false;
  } catch { /* a browser without matchMedia still needs the install */ }
  return true;
}

function NotificationsSection() {
  const [state, setState] = useState<'loading' | 'unsupported' | 'off' | 'on'>('loading');
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [prefs, setPrefs] = useState<PushPrefs | null>(null);
  const [busy, setBusy] = useState<'subscribe' | PushKind | null>(null);
  const [note, setNote] = useState<string | null>(null);
  /** True only when the ONE reason this device is incapable is a missing
   *  home-screen install (iOS) and the server is actually ready to serve
   *  push — otherwise the generic sentence stays, so the steps never point
   *  at an install that would still do nothing. */
  const [iosInstall, setIosInstall] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await api<{ configured: boolean; publicKey: string | null }>('/api/push/config');
        const secure = typeof window !== 'undefined' && window.location.protocol === 'https:';
        // iOS may expose PushManager in a plain Safari tab, but subscribing
        // there fails — outside a home-screen install there is no service
        // worker scope Web Push accepts, so the install decides capability.
        const needsInstall = secure && iosSafariNeedsInstall();
        const capable = Boolean(cfg?.configured) && secure && !needsInstall
          && 'serviceWorker' in navigator && 'PushManager' in window;
        if (cancelled) return;
        if (!capable) {
          setIosInstall(Boolean(cfg?.configured) && needsInstall);
          setState('unsupported');
          return;
        }
        setPublicKey(cfg.publicKey);
        const p = await api<{ preferences: PushPrefs }>('/api/push/preferences');
        if (cancelled) return;
        setPrefs({
          question: Boolean(p?.preferences?.question),
          partner: Boolean(p?.preferences?.partner),
          trial: Boolean(p?.preferences?.trial),
        });
        const reg = await navigator.serviceWorker.getRegistration();
        const sub = await reg?.pushManager?.getSubscription();
        if (!cancelled) setState(sub ? 'on' : 'off');
      } catch (e) {
        console.error('[Pear] push preferences load failed', e);
        if (!cancelled) setState('unsupported');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const subscribe = async () => {
    if (!publicKey) return;
    setBusy('subscribe');
    setNote(null);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState('off');
        setNote(permission === 'denied'
          ? 'Notifications are blocked for this site — turn them on in your browser settings, then try again.'
          : 'No problem — you can turn notifications on any time.');
        return;
      }
      const reg = await navigator.serviceWorker.register('/service-worker.js');
      const sub = await reg.pushManager.getSubscription()
        ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKeyBytes(publicKey) as unknown as BufferSource });
      await api('/api/push/subscribe', { body: sub.toJSON() as unknown as Record<string, unknown> });
      setState('on');
    } catch (e) {
      console.error('[Pear] push subscribe failed', e);
      setNote('Couldn’t set up notifications on this device. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const setPref = async (kind: PushKind, on: boolean) => {
    setPrefs(prev => (prev ? { ...prev, [kind]: on } : prev));
    setBusy(kind);
    setNote(null);
    try {
      const p = await api<{ preferences: PushPrefs }>('/api/push/preferences', { method: 'PUT', body: { [kind]: on } });
      setPrefs({
        question: Boolean(p?.preferences?.question),
        partner: Boolean(p?.preferences?.partner),
        trial: Boolean(p?.preferences?.trial),
      });
    } catch (e) {
      console.error('[Pear] push preference save failed', e);
      setNote('Couldn’t save that. Try again.');
    } finally {
      setBusy(null);
    }
  };

  if (state === 'loading') return null;

  return (
    <section className="set-section" aria-labelledby="set-notifications-title" data-testid="push-notifications">
      <h3 className="set-section-title" id="set-notifications-title">
        Notifications
      </h3>
      {state === 'unsupported' ? (
        iosInstall ? (
          /* Slice 3: the iPhone affordance. Safari never fires
             beforeinstallprompt, so the install is instructions, not a button —
             and the last step lands back on this screen, where the switches
             appear the moment the app is on the home screen. */
          <div data-testid="push-ios-install">
            <p className="set-section-note">
              On iPhone, notifications come from the installed app — Safari tabs cannot send them:
            </p>
            <ol className="set-steps">
              <li>Tap the <b>Share</b> button in Safari.</li>
              <li>Choose <b>Add to Home Screen</b>, then <b>Add</b>.</li>
              <li>Open Pear from your home screen and come back here to switch notifications on.</li>
            </ol>
          </div>
        ) : (
          <p className="set-note">
            Notifications are not available on this device yet — Pear needs to be added to your home screen on iPhone, or opened in a supporting browser.
          </p>
        )
      ) : (
        <>
          <p className="set-section-note">
            Notifications say that something happened, never what you wrote — your lock screen stays yours.
          </p>
          {state === 'off' && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">This device</div>
                <div className="set-note">Turn on to get notified here when you are not in the app.</div>
              </div>
              <button
                type="button"
                className="set-row-btn"
                data-testid="push-turn-on"
                disabled={busy !== null}
                onClick={() => void subscribe()}
              >
                {busy === 'subscribe' ? 'Setting up…' : 'Turn on'}
              </button>
            </div>
          )}
          {state === 'on' && (
            <p className="set-note" data-testid="push-on-this-device">On for this device.</p>
          )}
          {PUSH_ROWS.map(row => (
            <div className="set-row" key={row.kind} data-testid={`push-${row.kind}-row`}>
              <div className="set-text">
                <div className="set-label">{row.label}</div>
                <div className="set-note">{row.note}</div>
              </div>
              <Switch
                checked={Boolean(prefs?.[row.kind])}
                label={row.label}
                busy={busy === row.kind}
                disabled={busy !== null || prefs === null}
                onChange={on => void setPref(row.kind, on)}
              />
            </div>
          ))}
          {note && <p className="set-note" data-testid="push-note">{note}</p>}
        </>
      )}
    </section>
  );
}

export function Settings({ onClose, onLeave, onSignOut, onOpenAI, onOpenHelp, onOpenTour, pendingCode, awaitingPartner, mailEnabled, tab, onTabChange }: ExtendedProps) {
  const [toggles, setToggles] = useState<Toggles | null>(null);
  const [failed, setFailed] = useState(false);
  const [confirming, setConfirming] = useState(false);
  /** Card ocJz1TeLT4pi — 'Download my data first' lets a leaver keep their
   *  private half before the couple is gone. `exporting` disables the export
   *  button while the bundle is in flight; `exportMsg` reports success/error
   *  in the confirm sheet's status line. */
  const [exporting, setExporting] = useState(false);
  const [exportMsg, setExportMsg] = useState<string | null>(null);
  /** Card Vf23eSKKxDhX — Leave is a server call now, so it has a flight of
   *  its own: `leaving` disables the confirm button (one tap, one request),
   *  and `leaveError` carries the server's refusal back into the shared
   *  status line. A refusal keeps `confirming` open — the person is still in
   *  the couple and the reason is worth reading before they try again. */
  const [leaving, setLeaving] = useState(false);
  const [leaveError, setLeaveError] = useState<string | null>(null);

  const [profile, setProfile] = useState<Profile | null>(null);
  const [settings, setSettings] = useState<CoupleSettings | null>(null);
  /** The two names (card yFLCjm7ONIEc). `realName` is the account's own
   *  record (D1), `chatName` is what the partner sees in the room. null =
   *  not loaded yet; `nameBlocked` says why the real-name field is not there. */
  const [realName, setRealName] = useState<string | null>(null);
  const [nameBlocked, setNameBlocked] = useState<string | null>(null);
  const [chatName, setChatName] = useState<string | null>(null);
  /** Autosave is per keystroke, so each field tracks its own newest request:
   *  a slow response for an earlier keystroke must not repaint a newer value
   *  or clear the other field's status line. */
  const nameSeq = useRef(0);
  const chatSeq = useRef(0);
  /** Once someone has typed in a name field, a later load() (another row's
   *  failed save reloads everything) must not overwrite what they are in the
   *  middle of writing — that would be the silent rollback this card bans. */
  const nameDirty = useRef(false);
  const chatDirty = useRef(false);
  /** The couple's shared handle (card w8Mj4ThRii6Z). `handle` is what the
   *  server holds, `handleDraft` what is typed but not yet saved — a failed
   *  save keeps the draft in the field, exactly like the name rows. */
  const [handle, setHandle] = useState<string | null>(null);
  const [handleDraft, setHandleDraft] = useState<string | null>(null);
  const handleDirty = useRef(false);
  /** Times a week this member wants a partner digest — 0 is off (card g3ixXFWzhgJY). */
  const [digestFreq, setDigestFreq] = useState<number>(0);
  /** Weekly recap opt-in (card 3UwJvUtiirGN): the 'recap' AI toggle, plus
   *  the window so this row can say when the last one went out and, with
   *  nextDueAt, when the next one may go (card si3nCYgCqe4g). */
  const [recap, setRecap] = useState<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number } | null>(null);
  const [recapNote, setRecapNote] = useState<string | null>(null);
  /** One send attempt per mount — the window closes server-side, but a
   *  Settings re-render must never queue a second copy of the same week. */
  const recapTried = useRef(false);
  const [saving, setSaving] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Pear Plus (card 32BEnNO6I2kx): the plan row. Null until the state call
   *  lands; a load failure leaves it null and the row simply stays out of the
   *  way — Settings must never look broken because billing is unconfigured. */
  const [billing, setBilling] = useState<{ plus: boolean; trialing: boolean; granted: boolean; label: string; note: string; trialEnd: number | null; periodEnd: number | null; configured: boolean } | null>(null);

  const load = useCallback(() => {
    api<{ toggles: Toggles }>('/api/ai/toggles')
      .then(data => {
        setToggles(data.toggles ?? {});
        setFailed(false);
      })
      .catch(err => {
        // A page teardown cancels this request; that is not a load failure and
        // must not paint Settings as broken (card d0Md65M0shSI).
        if (isAbort(err)) return;
        console.error('[Pear] settings: toggles load failed', err);
        setFailed(true);
      });
    api<{ profile: Profile; settings: CoupleSettings; handle: string | null }>('/api/settings')
      .then(data => {
        setProfile(data.profile);
        setSettings(data.settings);
        // The chat name seeds from the name captured at pair-creation, so
        // the field is never blank on first open (card yFLCjm7ONIEc).
        if (!chatDirty.current) setChatName(data.profile.displayName ?? '');
        if (!handleDirty.current) {
          setHandle(data.handle ?? null);
          setHandleDraft(null);
        }
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] settings load failed', err); });
    // The real name lives on the account, which needs the ACCOUNT session —
    // a couple token cannot reach it. Pairing works without an account, so
    // say why rather than show a field that can never save, and do not fire
    // a request that can only answer 401.
    if (!getAuthSession()) {
      setRealName(null);
      setNameBlocked('Sign in to keep your real name on file. Your display name below works either way.');
    } else {
      authApi<{ account: AccountName }>('/api/auth/me')
        .then(data => {
          if (!nameDirty.current) setRealName(data.account?.name ?? '');
          setNameBlocked(null);
        })
        .catch(err => {
          // Teardown, not a failure — leave the row as it was (card d0Md65M0shSI).
          if (isAbort(err)) return;
          console.error('[Pear] settings: account name load failed', err);
          setRealName(null);
          setNameBlocked(authErrorMessage(err, 'Your name could not be loaded — try again.'));
        });
    }
    api<{ frequency: number }>('/api/digest')
      .then(data => setDigestFreq(Number(data.frequency) || 0))
      .catch(err => { if (!isAbort(err)) console.error('[Pear] digest frequency load failed', err); });
    // Pear Plus (card 32BEnNO6I2kx): the plan row reads the entitlement.
    //  A 401 (no couple token) or an unconfigured server leaves it null.
    api<{ entitlement: { plus: boolean; trialing?: boolean; trialEnd?: number | null; interval: string | null; currentPeriodEnd: number | null; subscriptionId: string | null; promoCode: string | null; promoEnd: number | null }; prices: Record<string, { label: string }>; configured: boolean }>('/api/billing/state')
      .then(data => {
        const e = data.entitlement;
        const price = e.interval ? data.prices[e.interval] : undefined;
        // A trial is Plus without a subscription (card ImIQwJDLW6Xs). It has
        // no Stripe customer yet, so "Manage" would open a portal that 404s —
        // the trial row offers Upgrade instead, and names its own end date.
        const trialing = Boolean(e.trialing);
        // A promo grant is the same trap for a different reason (card
        // Z7SGpA59WHSb): it is Plus, but it was GIVEN, so the couple has no
        // Stripe customer and no subscription to manage. Presenting it as a
        // subscription ("Renews <date>" + Manage) both misstates what they
        // have and dead-ends the portal. Name it as the grant it is.
        // It is checked BEFORE trialing because a granted couple still has a
        // live trialEnd (the trial clock keeps running underneath the grant),
        // so the server reports trialing: true — and "free trial" would be a
        // worse description of an indefinite grant than of a 30-day trial.
        const granted = Boolean(e.promoCode) && !e.subscriptionId;
        setBilling({
          plus: Boolean(e.plus),
          trialing,
          granted,
          label: granted
            ? 'Pear Plus · granted'
            : trialing
              ? 'Pear Plus · free trial'
              : e.plus
                ? `Pear Plus${price ? ` · ${price.label}` : ''}`
                : 'Pear Essentials',
          note: granted
            ? e.promoEnd
              ? `Granted to you until ${new Date(e.promoEnd).toLocaleDateString()} — nothing to pay, and no subscription to manage.`
              : 'Granted to you — nothing to pay, and no subscription to manage.'
            : trialing
              ? e.trialEnd
                ? `Free trial until ${new Date(e.trialEnd).toLocaleDateString()} — one subscription then covers you both.`
                : 'Free trial — one subscription then covers you both.'
              : e.plus
                ? e.currentPeriodEnd
                  ? `Renews ${new Date(e.currentPeriodEnd).toLocaleDateString()} — one subscription covers you both.`
                  : 'One subscription covers you both.'
                : 'The concierge services and weekly briefing are part of Pear Plus.',
          trialEnd: e.trialEnd ?? null,
          periodEnd: e.currentPeriodEnd ?? null,
          configured: Boolean(data.configured),
        });
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] billing state load failed', err); });
    api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap')
      .then(data => {
        const next = {
          enabled: Boolean(data.enabled),
          due: Boolean(data.due),
          lastSentAt: Number(data.lastSentAt) || 0,
          nextDueAt: Number(data.nextDueAt) || 0,
        };
        setRecap(next);
        // The cadence: opted in and the week has run → send it now, once.
        if (next.enabled && next.due && !recapTried.current) sendRecap();
      })
      .catch(err => { if (!isAbort(err)) console.error('[Pear] recap load failed', err); });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = useCallback(
    async (which: 'profile' | 'joint', body: Record<string, unknown>) => {
      setSaving(which);
      setSaveError(null);
      try {
        if (which === 'profile') {
          const data = await api<{ profile: Profile }>('/api/settings/profile', {
            method: 'PUT',
            body,
          });
          setProfile(data.profile);
        } else {
          const data = await api<{ settings: CoupleSettings }>('/api/settings/joint', {
            method: 'PUT',
            body,
          });
          setSettings(data.settings);
        }
      } catch (err) {
        console.error('[Pear] settings save failed', err);
        setSaveError('Couldn’t save that. Try again.');
        load();
      } finally {
        setSaving(null);
      }
    },
    [load],
  );

  /**
   * The two name fields autosave per keystroke (card yFLCjm7ONIEc). When the
   * server rejects a value, the typed text STAYS in the field: the server's
   * own sentence lands in the status line and the field takes focus, so a
   * failed save is never a silent rollback. Each field keeps its own request
   * counter so a slow response cannot repaint a newer keystroke.
   */
  const failName = (field: 'set-name' | 'set-display-name', message: string) => {
    setSaving(null);
    setSaveError(message);
    document.getElementById(field)?.focus();
  };

  const saveName = async (value: string): Promise<void> => {
    const seq = ++nameSeq.current;
    nameDirty.current = true;
    setRealName(value);
    setSaving('name');
    setSaveError(null);
    try {
      const data = await authApi<{ account: AccountName }>('/api/auth/profile', {
        method: 'PUT',
        body: { name: value },
      });
      if (seq !== nameSeq.current) return; // a newer keystroke owns the field
      setRealName(data.account?.name ?? value);
      setSaving(null);
    } catch (err) {
      if (seq !== nameSeq.current) return;
      console.error('[Pear] name save failed', err);
      failName('set-name', authErrorMessage(err, 'Couldn’t save that. Try again.'));
    }
  };

  const saveChatName = async (value: string): Promise<void> => {
    const seq = ++chatSeq.current;
    chatDirty.current = true;
    setChatName(value);
    setSaving('display');
    setSaveError(null);
    try {
      const data = await api<{ profile: Profile }>('/api/settings/profile', {
        method: 'PUT',
        body: { displayName: value },
      });
      if (seq !== chatSeq.current) return;
      const saved = data.profile?.displayName ?? value;
      setChatName(saved);
      setProfile(p => (p ? { ...p, displayName: saved } : p));
      setSaving(null);
    } catch (err) {
      if (seq !== chatSeq.current) return;
      console.error('[Pear] display name save failed', err);
      failName('set-display-name', authErrorMessage(err, 'Couldn’t save that. Try again.'));
    }
  };

  /** The handle saves on blur or Enter, not per keystroke: uniqueness is
   *  checked once, against the whole table, and a rejection must read as
   *  "taken", not as a typo. The typed text stays in the field on failure and
   *  the server's sentence lands in the status line (PRD §6 — the words are
   *  the reason). A successful save shows the normalised value back, so a
   *  person who typed "Velvet Moon" sees "velvet-moon". */
  const commitHandle = async (): Promise<void> => {
    const value = (handleDraft ?? handle ?? '').trim();
    if (!handle || value === handle) {
      handleDirty.current = false;
      setHandleDraft(null);
      return;
    }
    setSaving('handle');
    setSaveError(null);
    try {
      const data = await api<{ handle: string }>('/api/settings/handle', {
        method: 'PUT',
        body: { handle: value },
      });
      handleDirty.current = false;
      setHandle(data.handle);
      setHandleDraft(null);
      setSaving(null);
    } catch (err) {
      console.error('[Pear] handle save failed', err);
      setSaving(null);
      setSaveError(authErrorMessage(err, 'Couldn’t save that. Try again.'));
      document.getElementById('set-handle')?.focus();
    }
  };

  /** One PUT, optimistic like every other row: frequency is a small integer
   *  the DO validates, so the control can move before the round trip lands. */
  const saveDigestFrequency = async (frequency: number) => {
    const value = Math.max(0, Math.min(7, Math.round(frequency)));
    setDigestFreq(value);
    setSaving('digest');
    setSaveError(null);
    try {
      const data = await api<{ frequency: number }>('/api/digest/frequency', {
        method: 'PUT',
        body: { frequency: value },
      });
      setDigestFreq(data.frequency);
    } catch (err) {
      console.error('[Pear] digest frequency save failed', err);
      setSaveError('Couldn’t save that. Try again.');
      load();
    } finally {
      setSaving(null);
    }
  };

  /** Weekly recap (card 3UwJvUtiirGN): the 'recap' permission doubles as the
   *  opt-in, so this row writes the same toggle the concierge screen writes —
   *  one writer, no second table to drift. */
  const setRecapEnabled = async (on: boolean) => {
    setRecap(prev => (prev ? { ...prev, enabled: on } : { enabled: on, due: false, lastSentAt: 0, nextDueAt: 0 }));
    setSaving('recap');
    setSaveError(null);
    setRecapNote(null);
    try {
      await api<{ toggles: Toggles }>('/api/ai/toggles', { method: 'PUT', body: { category: 'recap', on } });
      const state = await api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap');
      setRecap({ enabled: state.enabled, due: state.due, lastSentAt: state.lastSentAt || 0, nextDueAt: state.nextDueAt || 0 });
      // Turning it on when the week has already run sends that week — the
      // window is not something the user should have to wait out twice.
      if (state.enabled && state.due && !recapTried.current) void sendRecap();
    } catch (err) {
      console.error('[Pear] recap toggle save failed', err);
      setSaveError('Couldn’t save that. Try again.');
      load();
    } finally {
      setSaving(null);
    }
  };

  /** The send itself. Called at most once per mount (recapTried), and the
   *  server refuses anything that is off, not due or empty — so the row can
   *  only ever report an honest reason. */
  const sendRecap = async () => {
    if (recapTried.current) return;
    recapTried.current = true;
    // What the server said before we asked, so a failure below can be
    // checked against what actually happened (card myEbyq9tpkJS).
    const lastSentBefore = recap?.lastSentAt ?? 0;
    setRecapNote('Sending this week’s recap…');
    try {
      const data = await api<{
        sent: boolean;
        reason: string;
        deliveries: { to: string; name?: string | null; sent: boolean; reason?: string }[];
      }>('/api/recap/send', { method: 'POST', body: {} });
      if (data.sent) {
        setRecap(prev => (prev ? { ...prev, due: false, lastSentAt: Date.now() } : prev));
        const rows = data.deliveries ?? [];
        const who = (d: { to: string; name?: string | null }) => d.name || d.to;
        const gone = rows.filter(d => d.sent).map(who);
        // A partner who did not get theirs is named — silence would read as
        // "everyone got it", which is exactly the lie this row must not tell.
        const missed = rows.filter(d => !d.sent && d.reason !== 'nothing_to_report').map(who);
        setRecapNote(
          missed.length
            ? `Sent to ${gone.join(', ')} — missed ${missed.join(', ')}.`
            : gone.length
              ? `Sent — ${gone.join(', ')}`
              : 'Sent.',
        );
      } else {
        setRecapNote(recapReason(data.reason));
      }
    } catch (err) {
      console.error('[Pear] recap send failed', err);
      // The mail may already be out: a non-2xx after delivery must never be
      // reported as a failure, so ask the server what it recorded before
      // claiming the send did not go (msg_6b9579f2).
      try {
        const st = await api<{ enabled: boolean; due: boolean; lastSentAt: number; nextDueAt: number }>('/api/recap');
        if (Number(st.lastSentAt) > lastSentBefore) {
          setRecap({ enabled: Boolean(st.enabled), due: Boolean(st.due), lastSentAt: Number(st.lastSentAt), nextDueAt: Number(st.nextDueAt) || 0 });
          setRecapNote('Sent — your recap is on its way.');
          return;
        }
      } catch {
        // No answer either way — fall through to the honest failure line.
      }
      recapTried.current = false; // a transport error may be transient
      setRecapNote('Couldn’t send that. Try again later.');
    }
  };

  const patchHolidays = (next: Holiday[]) => {
    if (!settings) return;
    setSaveError(null); // a rejected toggle's message should not outlive the next edit
    setSettings({ ...settings, holidays: next });
    void save('joint', { holidays: next });
  };

  /** Clamp 1-365 exactly as the input always has, then save through the same
   *  path — the stepper and preset chips are just other ways to pick a number. */
  const setLead = (index: number, raw: number): void => {
    if (!settings) return;
    const leadDays = Math.max(1, Math.min(365, Math.round(raw)));
    const next = [...settings.holidays];
    next[index] = { ...next[index], leadDays };
    patchHolidays(next);
  };

  const customCount = settings ? settings.holidays.filter(h => isCustomKey(h.key)).length : 0;

  const addCustom = () => {
    if (!settings || customCount >= MAX_CUSTOM) return;
    patchHolidays([
      ...settings.holidays,
      { key: `custom-${customCount + 1}`, label: '', enabled: false, leadDays: 30, date: null },
    ]);
  };

  const anyPermission = toggles ? Object.values(toggles).some(Boolean) : false;
  const status = leaveError ? leaveError : saveError ? saveError : saving ? 'Saving…' : '';

  /**
   * Card Vf23eSKKxDhX — the confirm tap, and the whole reason Leave had to
   * change. It used to clear browser storage and call it done, which left
   * `accounts.couple_id` pointing at the room: the next sign-in pulled the
   * same person straight back in. Now the server goes first, and a refusal
   * leaves everything exactly as it was — screen open, couple intact, reason
   * in the status line above. A Leave that half-happens is worse than one
   * that did not.
   */
  const confirmLeave = async (): Promise<void> => {
    if (leaving) return;
    setLeaving(true);
    setLeaveError(null);
    const failed = await onLeave();
    // Success unmounts this screen (App closes it), so the state below only
    // ever runs on the refusal path.
    setLeaving(false);
    if (failed) setLeaveError(failed);
  };

  /** Card ocJz1TeLT4pi — fetch this member's private half as a bundle and
   *  save it as a download, before Leave is confirmed. Failure (out of
   *  couple, server down) reports in the confirm sheet instead of blocking
   *  the leave itself — the export is an offer, never a gate. */
  const downloadMyData = async (): Promise<void> => {
    if (exporting) return;
    setExporting(true);
    setExportMsg(null);
    try {
      const bundle = await exportMeRequest();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pear-my-data.json';
      a.click();
      URL.revokeObjectURL(url);
      setExportMsg('Your bundle is downloading — keep it, then leave whenever you are ready.');
    } catch (e) {
      setExportMsg(authErrorMessage(e, 'Could not build your bundle — try again.'));
    } finally {
      setExporting(false);
    }
  };

  /** Section navigation (card DI31pLXCFc2C): a tap on a tab switches the
   *  section — the tab row IS the navigation, pinned while content scrolls.
   *  The hash follows so the URL is always a shareable link to what shows. */
  const selectTab = useCallback((key: string) => {
    onTabChange(key);
    if (window.location.hash !== `#settings-${key}`) {
      window.history.replaceState(null, '', `#settings-${key}`);
    }
    // Land the section at the top of the scrollport once it mounts.
    window.requestAnimationFrame(() => {
      document.getElementById('settings-screen')?.scrollIntoView({ block: 'start' });
    });
  }, [onTabChange]);

  return (
    <div className="set-panel" id="settings-screen">
      <div className="set-head">
        <h2 className="set-title">Settings</h2>
        <div className="set-head-actions">
          <button
            type="button"
            className="pear-btn set-done"
            id="btn-settings-done"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>

      <span className="set-status" aria-live="polite">{status}</span>

      <SubTabs
        name="settings"
        label="Settings sections"
        tabs={SETTINGS_TABS}
        active={tab}
        onChange={selectTab}
        sticky
      />
      <SubPanel name="settings" tabKey={tab}>

        {/* ── Invite, until it is accepted (Jonathan, pm admin 2026-10-13) ──
            The chat share bar is the only place the invite used to live, and
            it disappears the moment you navigate. Settings is the screen people
            come back to, so the same card sits at the top of the You tab for
            as long as the couple is still one member: Copy / Share / Email /
            QR, and the waiting line that says what is still outstanding.
            Leading the identity tab because pairing IS the identity to-do. */}
        {tab === 'you' && awaitingPartner && pendingCode && (
          <section className="set-section" aria-labelledby="set-invite-title">
            <h3 className="set-section-title" id="set-invite-title">
              Invite your partner
            </h3>
            <p className="set-section-note">
              This stays here until they join — send it however suits you, as
              often as you like.
            </p>
            <InviteCard
              code={pendingCode}
              mailEnabled={Boolean(mailEnabled)}
              idPrefix="settings-invite"
              variant="pair"
            />
          </section>
        )}

        {/* ── Share Pear: the referral link (card 3MWZ8Zs7jA7e) ────────────
            Distinct from the invite above: that one brings the partner in,
            this one brings other COUPLES in — both sides earn 30 days of Plus
            (Jonathan 2026-10-17, JitsLab parity). Shown to any signed-in
            couple, paired or not: a link is shareable the day it is minted,
            and a friend who arrived on someone's link needs to see their own
            (the card is where the vested count lands).

            It lives on the PLAN tab (card sWEYh0kZBtRY, Jonathan 2026-10-20):
            it is a commercial offer about terms, so it sits beside the plan
            and its prices, not among the identity rows. Gated on the tab, not
            on `billing` — a couple whose billing state has not loaded still
            gets their own link. */}

        {/* ── You: identity, one person's own rows ─────────────────────── */}
        {tab === 'you' && (
        <section className="set-section" aria-labelledby="set-you-title">
          <h3 className="set-section-title" id="set-you-title">
            You
          </h3>

          {/* ── The two names (card yFLCjm7ONIEc). Two questions, two fields:
              the real name is the account's own record and never enters the
              thread; the display name is the room's members row, which is what
              the partner actually sees. Both autosave per keystroke. ─────── */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Name</div>
              <div className="set-note">Your real name — account records only, never shown in the chat.</div>
            </div>
            {nameBlocked ? (
              <p className="set-note set-name-blocked" id="set-name-blocked">{nameBlocked}</p>
            ) : realName === null ? (
              <p className="set-loading" aria-busy="true">Loading…</p>
            ) : (
              <input
                type="text"
                id="set-name"
                className="set-name-input"
                aria-label="Your name"
                autoComplete="name"
                value={realName}
                onChange={e => void saveName(e.target.value)}
              />
            )}
          </div>

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Display name</div>
              <div className="set-note">What your partner sees in the thread and in presence.</div>
            </div>
            <input
              type="text"
              id="set-display-name"
              className="set-name-input"
              aria-label="Display name"
              value={chatName ?? ''}
              onChange={e => void saveChatName(e.target.value)}
            />
          </div>

          {/* Reply-text colour (card 9QADJv3CdiOh, founder email 2026-10-23):
              the ink YOUR words wear in the partner's feed. The swatches are
              the closed shared list — each one measured AA on the mustard
              partner bubble — and the preview is the partner's own view of a
              bubble, so what is picked is what they will read. One PUT per
              click through the same save() as the birthday row; a refusal
              lands in the status line and load() restores the server's truth. */}
          <div className="set-row set-colour-row">
            <div className="set-text">
              <div className="set-label">Reply colour</div>
              <div className="set-note">The colour your replies wear in your partner’s feed.</div>
              <div className="set-colour-preview" aria-hidden="true">
                <span
                  className="set-colour-preview-bubble"
                  style={{ color: replyColourHex(profile?.replyColour ?? null) ?? '#1F3B2D' }}
                >
                  Your words, their feed.
                </span>
              </div>
            </div>
            <div className="set-colour-grid" role="radiogroup" aria-label="Reply colour">
              {REPLY_COLOURS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={(profile?.replyColour ?? null) === c.key}
                  aria-label={c.label}
                  title={c.label}
                  className={`set-colour-swatch${profile?.replyColour === c.key ? ' is-on' : ''}`}
                  style={{ background: c.hex }}
                  onClick={() => void save('profile', { replyColour: c.key })}
                />
              ))}
            </div>
          </div>

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Birthday</div>
              <div className="set-note">Yours only — your partner keeps their own.</div>
            </div>
            <input
              type="date"
              id="set-birthday"
              className="set-date"
              aria-label="Your birthday"
              value={profile?.birthday ?? ''}
              onChange={e => {
                const value = e.target.value || null;
                if (value && !isDate(value)) return;
                setProfile(p =>
                  p
                    ? { ...p, birthday: value }
                    : { memberId: '', birthday: value, displayName: chatName ?? null },
                );
                void save('profile', { birthday: value });
              }}
            />
          </div>

          {/* Partner digest cadence (card g3ixXFWzhgJY) — the JitsLab
              digest_frequency shape: 0 is off, and off is a complete answer. */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Partner digest</div>
              <div className="set-note">How often one arrives in Us, built from your partner’s interests.</div>
            </div>
            <select
              id="set-digest-frequency"
              className="set-date"
              aria-label="Partner digest frequency"
              value={digestFreq}
              onChange={e => void saveDigestFrequency(Number(e.target.value))}
            >
              <option value={0}>Off</option>
              {[1, 2, 3, 4, 5, 6, 7].map(n => (
                <option key={n} value={n}>{n}× a week</option>
              ))}
            </select>
          </div>

          {/* Weekly recap (card 3UwJvUtiirGN, Jonathan email 2026-10-07) —
              opt-in, one email a week to each of you, each with their own half
              of the week. The toggle is the 'recap' permission, so the
              concierge screen shows the same state. */}
          <div className="set-row" id="recap-row">
            <div className="set-text">
              <div className="set-label">Weekly recap</div>
              <div className="set-note">
                One email a week: questions answered, decisions taken, links filed. Yours only — your partner gets their own.
              </div>
              {recap?.enabled && (
                <p className="set-note" id="recap-next" data-testid="recap-next">{recapWhen(recap)}</p>
              )}
              {recapNote && (
                <p className="set-note" id="recap-note">{recapNote}</p>
              )}
            </div>
            <Switch
              checked={Boolean(recap?.enabled)}
              label="Weekly recap"
              disabled={saving === 'recap' || recap === null}
              onChange={on => void setRecapEnabled(on)}
            />
          </div>

          {/* Interests profile (card 0OvyJbpnACMX) — the agent's four
              questions, one at a time, then the topic chips it builds. It is
              YOUR profile, so it lives on this tab. */}
          <section className="set-section" aria-labelledby="set-interests-title">
            <h3 className="set-section-title" id="set-interests-title">
              Your interests
            </h3>
            <Interests />
          </section>

          {/* Notifications (card HWrVePJ5qW4d slice 2) — the three triggers. */}
          <NotificationsSection />
        </section>
        )}

        {/* ── Us: the shared half ────────────────────────────────────── */}
        {tab === 'us' && (
        <section className="set-section" aria-labelledby="set-us-title">
          <h3 className="set-section-title" id="set-us-title">
            Us
          </h3>

          {/* ── Shared handle (card w8Mj4ThRii6Z, accounts Phase E) — the
              couple's own name, minted at pair-creation and replaceable here.
              It doubles as a sign-in identifier, which is why the note says so. */}
          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Handle</div>
              <div className="set-note">
                Your couple's name — one you can also sign in with.
              </div>
            </div>
            {handle === null ? (
              <p className="set-loading" aria-busy="true">Loading…</p>
            ) : (
              <input
                type="text"
                id="set-handle"
                className="set-name-input"
                aria-label="Handle"
                maxLength={40}
                spellCheck={false}
                autoComplete="off"
                value={handleDraft ?? handle}
                onChange={e => {
                  handleDirty.current = true;
                  setHandleDraft(e.target.value);
                }}
                onBlur={() => void commitHandle()}
                onKeyDown={e => {
                  if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                }}
              />
            )}
          </div>

          {/* ── Shared inbox address (card qntf9Qa0Cemr) — where the couple
              emails so Pear can take it in. Built from the server-issued code,
              same value the Admin menu and invite moment show. */}
          <InboxAddress
            code={getCoupleCode()}
            idPrefix="settings"
            variant="row"
            note="Email bills, bookings and receipts here — the concierge sorts each one into your shared space."
          />

          <div className="set-row">
            <div className="set-text">
              <div className="set-label">Anniversary</div>
              <div className="set-note">The date you two count from.</div>
            </div>
            <input
              type="date"
              id="set-anniversary"
              className="set-date"
              aria-label="Anniversary"
              value={settings?.anniversary ?? ''}
              onChange={e => {
                const value = e.target.value || null;
                if (value && !isDate(value)) return;
                if (settings) setSettings({ ...settings, anniversary: value });
                void save('joint', { anniversary: value });
              }}
            />
          </div>

          <div className="set-mode" role="group" aria-labelledby="set-mode-label">
            <div className="set-text">
              <div className="set-label" id="set-mode-label">From your inbox</div>
              <div className="set-note">
                When a bill or booking lands: ask us first, or file it as a to-do
                straight away. The to-do permission in Settings → Concierge is the off switch.
              </div>
            </div>
            <div className="set-mode-options">
              {(['suggest', 'auto'] as const).map(mode => (
                <button
                  type="button"
                  key={mode}
                  id={mode === 'suggest' ? 'set-mode-suggest' : 'set-mode-auto'}
                  role="radio"
                  aria-checked={settings?.inboxTodoMode === mode}
                  className={`set-mode-btn${settings?.inboxTodoMode === mode ? ' is-on' : ''}`}
                  disabled={saving === 'joint'}
                  onClick={() => {
                    if (!settings || settings.inboxTodoMode === mode) return;
                    setSettings({ ...settings, inboxTodoMode: mode });
                    void save('joint', { inboxTodoMode: mode });
                  }}
                >
                  {mode === 'suggest' ? 'Ask us first' : 'Add automatically'}
                </button>
              ))}
            </div>
          </div>

          {/* ── Task Assist (spec kZX3gsJkKaTH) — the couple-level consent
              switch for the chore-balance engine. One row, so both partners
              read the same state, and off until someone says yes. The
              one-time offer that points here lives in the Life rail; this is
              the standing door, findable without waiting to be offered. ── */}
          <div className="set-mode" role="group" aria-labelledby="set-task-assist-label">
            <div className="set-text">
              <div className="set-label" id="set-task-assist-label">Task Assist</div>
              <div className="set-note">
                If it looks like something is sliding, I’ll suggest an approach
                that may work better for everyone.
              </div>
            </div>
            <div className="set-mode-options">
              {([true, false] as const).map(on => (
                <button
                  type="button"
                  key={String(on)}
                  id={on ? 'set-task-assist-on' : 'set-task-assist-off'}
                  role="radio"
                  aria-checked={(settings?.taskAssist?.enabled ?? false) === on}
                  className={`set-mode-btn${(settings?.taskAssist?.enabled ?? false) === on ? ' is-on' : ''}`}
                  disabled={saving === 'joint'}
                  onClick={() => {
                    if (!settings) return;
                    if ((settings.taskAssist?.enabled ?? false) === on) return;
                    setSettings({ ...settings, taskAssist: { enabled: on, offer: false } });
                    void save('joint', { taskAssist: on });
                  }}
                >
                  {on ? 'On' : 'Off'}
                </button>
              ))}
            </div>
          </div>

          <div className="set-group">
            <div className="set-group-head">
              <span className="set-group-title">Occasions</span>
              <span className="set-group-note">
                How far ahead the concierge starts asking — off means no reminder for that one.
              </span>
            </div>

            {!settings && <p className="set-loading" aria-busy="true">Loading…</p>}

            {settings?.holidays.map((h, i) => {
              const custom = isCustomKey(h.key);
              const name = h.label || (custom ? 'custom occasion' : h.key);
              return (
                <div className="set-row set-holiday" key={h.key}>
                  <div className="set-text">
                    <div className="set-label">
                      {custom ? (
                        <input
                          type="text"
                          id={`set-custom-label-${i}`}
                          className="set-text-input set-custom-label"
                          placeholder="Name a custom occasion"
                          maxLength={60}
                          aria-label="Custom occasion name"
                          value={h.label}
                          onChange={e => {
                            const next = [...settings.holidays];
                            next[i] = { ...h, label: e.target.value };
                            patchHolidays(next);
                          }}
                        />
                      ) : (
                        h.label || h.key
                      )}
                    </div>

                    {custom && (
                      <div className="set-note set-custom-date">
                        <label className="set-custom-date-label" htmlFor={`set-custom-date-${i}`}>
                          Date
                        </label>
                        <input
                          type="date"
                          id={`set-custom-date-${i}`}
                          className="set-date set-custom-date-input"
                          aria-label={`${name} date — repeats every year`}
                          value={h.date ? `${DATE_YEAR}-${h.date}` : ''}
                          onChange={e => {
                            const picked = e.target.value;
                            const next = [...settings.holidays];
                            next[i] = { ...h, date: picked ? picked.slice(5) : null };
                            patchHolidays(next);
                          }}
                        />
                        <span className="set-custom-date-note">Repeats every year</span>
                      </div>
                    )}

                    <div className="set-note set-lead">
                      <span>Remind us</span>
                      <div className="set-stepper">
                        <button
                          type="button"
                          className="set-stepper-btn"
                          aria-label={`${name} lead time minus`}
                          disabled={h.leadDays <= 1}
                          onClick={() => setLead(i, h.leadDays - 1)}
                        >
                          &minus;
                        </button>
                        <input
                          type="number"
                          className="set-lead-input"
                          aria-label={`${name} lead time in days`}
                          min={1}
                          max={365}
                          value={h.leadDays}
                          onChange={e => {
                            const raw = Number(e.target.value);
                            if (!Number.isFinite(raw)) return;
                            setLead(i, raw);
                          }}
                        />
                        <button
                          type="button"
                          className="set-stepper-btn"
                          aria-label={`${name} lead time plus`}
                          disabled={h.leadDays >= 365}
                          onClick={() => setLead(i, h.leadDays + 1)}
                        >
                          +
                        </button>
                      </div>
                      <span>days ahead</span>
                      <div className="set-presets" role="group"
                        aria-label={`${name} lead time presets`}>
                        {PRESET_LEADS.map(d => (
                          <button
                            key={d}
                            type="button"
                            className={`set-preset-chip${h.leadDays === d ? ' is-on' : ''}`}
                            aria-pressed={h.leadDays === d}
                            onClick={() => setLead(i, d)}
                          >
                            {d}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="set-row-side">
                    <Switch
                      checked={h.enabled}
                      label={`${name} reminder`}
                      disabled={saving === 'joint'}
                      onChange={() => {
                        // The DO refuses to arm a custom occasion with no name or
                        // no date — say so here instead of PUTting a guaranteed 400.
                        if (custom && !h.enabled && (!h.label.trim() || !h.date)) {
                          const needsDate = !h.date;
                          setSaveError(needsDate ? 'Give it a date first.' : 'Give it a name first.');
                          document
                            .getElementById(needsDate ? `set-custom-date-${i}` : `set-custom-label-${i}`)
                            ?.focus();
                          return;
                        }
                        const next = [...settings.holidays];
                        next[i] = { ...h, enabled: !h.enabled };
                        patchHolidays(next);
                      }}
                    />
                    {custom && (
                      <button
                        type="button"
                        className="set-remove"
                        id={`set-remove-${i}`}
                        disabled={saving === 'joint'}
                        aria-label={`Remove ${name}`}
                        onClick={() => patchHolidays(settings.holidays.filter((_, j) => j !== i))}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              );
            })}

            {settings && customCount < MAX_CUSTOM && (
              <button
                type="button"
                className="set-add-occasion"
                id="btn-add-occasion"
                disabled={saving === 'joint'}
                onClick={addCustom}
              >
                Add another occasion
              </button>
            )}
          </div>
        </section>
        )}

        {/* ── Concierge: the agent's switches ────────────────────────── */}
        {tab === 'concierge' && (
        <section className="set-section" aria-labelledby="set-agent-title">
          <h3 className="set-section-title" id="set-agent-title">
            Concierge
          </h3>
          <p className="set-section-note">
            Permissions live here — turn one on, then open the concierge screen.
          </p>

          <div className="set-row set-agent-toggle">
            <div className="set-text">
              <div className="set-label">Open the concierge</div>
              <div className="set-note">View chat history, inbox permissions and access log.</div>
            </div>
            {onOpenAI && (
              <button
                type="button"
                className="pear-btn pear-btn-mustard set-agent-btn"
                id="btn-open-ai"
                onClick={() => onOpenAI()}
              >
                Open
              </button>
            )}
          </div>

          {toggles === null && !failed && (
            <p className="set-loading" aria-busy="true">
              Loading…
            </p>
          )}
          {failed && (
            <p className="set-error">
              Couldn’t load your permissions. Try again.
            </p>
          )}
          {toggles !== null && !anyPermission && (
            <p className="set-empty">Every permission is off — nothing to configure yet.</p>
          )}

          <AgentSettings visible={anyPermission} />
        </section>
        )}

        {/* ── Plan (card 32BEnNO6I2kx) ───────────────────────────── */}
        {tab === 'plan' && billing && (
          <section className="set-section" aria-labelledby="set-plan-title">
            <h3 className="set-section-title" id="set-plan-title">
              Plan
            </h3>
            <div className="set-row" data-testid="settings-plan-row">
              <div className="set-text">
                <div className="set-label">{billing.label}</div>
                <div className="set-note" data-testid="settings-plan-note">
                  {billing.note}
                </div>
              </div>
              <button
                type="button"
                className="pear-btn set-row-btn"
                id="btn-plan-action"
                onClick={() => {
                  // Both paths go through the sheet: Plus opens the portal from
                  // inside it, Essentials gets the two prices.
                  void import('../../lib/billing').then(m => m.announcePlusRequired());
                }}
              >
                {/* A grant has no Stripe customer, so there is no portal to
                    open — it offers Upgrade (convert to a real plan) the way
                    the trial row does (card Z7SGpA59WHSb). */}
                {billing.plus && !billing.trialing && !billing.granted ? 'Manage' : 'Upgrade'}
              </button>
            </div>
            {!billing.configured && (
              <p className="set-section-note">Billing is not switched on for this server yet.</p>
            )}
          </section>
        )}

        {/* Share Pear on the Plan tab (card sWEYh0kZBtRY). Tab-gated only, so
            the couple's link is there whether or not billing has loaded. */}
        {tab === 'plan' && <SharePearCard />}

        {/* ── About: leaving + build (the account-level rows) ─────────── */}
        {tab === 'about' && (
        <>
        {/* ── Help ───────────────────────────────────────────────────── */}
        {/* The full feature inventory (card FiLhF6LRwAAP) lives one tap from
            here: About is where people look when they are lost. */}
        {onOpenHelp && (
          <section className="set-section" aria-labelledby="set-help-title">
            <h3 className="set-section-title" id="set-help-title">
              Help
            </h3>
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Every feature, explained</div>
                <div className="set-note">
                  What each thing is, why it helps the two of you, and one
                  example of it in a real week.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn set-row-btn"
                id="btn-open-help"
                onClick={() => onOpenHelp()}
              >
                Open
              </button>
            </div>
            {/* The tour nests here (card yOszUUb0Nnc2): a ten-second walk
                through the app belongs with the explanation, not as a
                top-level row competing with the destinations themselves. */}
            {onOpenTour && (
              <div className="set-row">
                <div className="set-text">
                  <div className="set-label">Take the tour</div>
                  <div className="set-note">
                    Nine short stops through what Pear does, on demand. It
                    never opens by itself.
                  </div>
                </div>
                <button
                  type="button"
                  className="pear-btn set-row-btn"
                  id="btn-settings-tour"
                  onClick={() => onOpenTour()}
                >
                  Start
                </button>
              </div>
            )}
          </section>
        )}

        {/* ── Leaving ────────────────────────────────────────────────── */}
        <section className="set-section" aria-labelledby="set-danger-title">
          <h3 className="set-section-title set-section-title-danger" id="set-danger-title">
            Leaving
          </h3>

          {!confirming && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Sign out</div>
                <div className="set-note">
                  Ends your session on this device. The couple stays exactly as
                  it is — sign back in with your email and password.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn set-row-btn"
                id="btn-signout"
                onClick={() => onSignOut?.()}
              >
                Sign out
              </button>
            </div>
          )}

          {!confirming && (
            <div className="set-row">
              <div className="set-text">
                <div className="set-label">Leave this couple</div>
                <div className="set-note">
                  Your partner keeps the space. This account stops belonging to it
                  straight away — sign back in and you start from the pair screen.
                </div>
              </div>
              <button
                type="button"
                className="pear-btn pear-btn-blush set-row-btn"
                id="btn-leave"
                onClick={() => { setLeaveError(null); setConfirming(true); }}
              >
                Leave
              </button>
            </div>
          )}

          {confirming && (
            <div className="set-confirm" role="group" aria-label="Confirm leaving">
              <p className="set-confirm-copy">
                Leave for good? You can’t undo this from here — rejoining needs a
                new pair code from your partner, and any Pear Plus subscription on
                this couple has to end first.
              </p>
              <div className="set-confirm-data">
                <div className="set-confirm-data-title">What happens to my data</div>
                <ul className="set-confirm-data-list">
                  <li>Your shared things — the thread, questions, gifts and dates — stay with your partner.</li>
                  <li>Your private things — private notes, private gift lists, your interests profile — are downloaded by you, then removed from this couple.</li>
                </ul>
              </div>
              <div className="set-confirm-actions">
                <button
                  type="button"
                  className="pear-btn set-cancel"
                  id="btn-export-data"
                  onClick={() => void downloadMyData()}
                  disabled={exporting || leaving}
                >
                  {exporting ? 'Building…' : 'Download my data first'}
                </button>
                {exportMsg && <p className="set-note" id="export-msg">{exportMsg}</p>}
                <button
                  type="button"
                  className="pear-btn set-cancel"
                  id="btn-leave-cancel"
                  onClick={() => { setConfirming(false); setLeaveError(null); }}
                >
                  Stay
                </button>
                <button
                  type="button"
                  className="pear-btn pear-btn-blush"
                  id="btn-leave-confirm"
                  onClick={() => void confirmLeave()}
                  disabled={leaving}
                >
                  {leaving ? 'Leaving…' : 'Leave couple'}
                </button>
              </div>
            </div>
          )}
        </section>

        {/* ── Build freshness — the founder asked for an ongoing cache buster */}
        <section className="set-section" aria-labelledby="set-build-title">
          <h3 className="set-section-title" id="set-build-title">
            Build
          </h3>
          <div className="set-row">
            <div className="set-text">
              <div className="set-label" id="set-build-id">{BUILD_ID}</div>
              <div className="set-note">
                Not seeing a change you expected? Reload pulls the latest build.
              </div>
            </div>
            <button
              type="button"
              className="pear-btn set-row-btn"
              id="btn-reload-latest"
              onClick={() => window.location.assign(`/?cb=${Date.now()}`)}
            >
              Reload latest
            </button>
          </div>
          {/* Card QR9phRSRQHNx — what's new in this build, straight under the
              reference it belongs to. */}
          {WHATS_NEW_FOR_BUILD.map(entry => (
            <div className="set-whatsnew" key={entry.version + entry.date} data-testid="set-whatsnew">
              <p className="set-note" id="set-whatsnew-title">
                What&apos;s new · {entry.version === 'rolling' ? BUILD_ID : entry.version} ({entry.date})
              </p>
              <ul className="set-whatsnew-list">
                {entry.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ))}
        </section>
        </>
        )}
      </SubPanel>

    </div>
  );
}
